import { useCallback, useEffect, useMemo, useRef } from "react";
import type { EventNode, TypedEvent } from "@/shared/lib/componentHierarchy";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import type {
  ModalContextValue,
  ModalRegistration,
  ModalRegistrationOptions,
} from "@/shared/ui/overlay/modal-context";
import {
  createModalStackMediator,
  isModalStackEvent,
  type ModalStackEffect,
  type ModalStackEvent,
} from "@/shared/ui/overlay/modalStackMediator";

type BodyScrollLockSnapshot = [
  scrollY: number,
  bodyStyle: string,
  rootOverflow: string,
  fixedBody: boolean,
];

export function useModalPresenter(fixedBodyScrollLock: boolean): {
  node: EventNode<TypedEvent>;
  value: ModalContextValue;
} {
  const bodyScrollLockSnapshotRef = useRef<BodyScrollLockSnapshot | null>(null);
  const fixedBodyScrollLockRef = useRef(fixedBodyScrollLock);
  fixedBodyScrollLockRef.current = fixedBodyScrollLock;

  const syncBodyScrollLock = useCallback((shouldLock: boolean): void => {
    if (shouldLock && !bodyScrollLockSnapshotRef.current) {
      const { style } = document.body;
      const rootStyle = document.documentElement.style;
      const scrollY = window.scrollY;
      const fixedBody = fixedBodyScrollLockRef.current;
      bodyScrollLockSnapshotRef.current = [
        scrollY,
        style.cssText,
        rootStyle.getPropertyValue("overflow"),
        fixedBody,
      ];
      if (!fixedBody) {
        // PCのwindow仮想リストが現在行を維持できるよう、実スクロール座標は動かさない。
        rootStyle.overflow = "hidden";
      } else {
        // iOSではoverflowだけでは背景スクロールを防げないためbodyを固定する。
        style.cssText += `;overflow:hidden;position:fixed;top:${-scrollY}px;left:0;right:0;width:auto`;
      }
    } else if (!shouldLock && bodyScrollLockSnapshotRef.current) {
      const [scrollY, bodyStyle, rootOverflow, fixedBody] =
        bodyScrollLockSnapshotRef.current;
      bodyScrollLockSnapshotRef.current = null;
      if (fixedBody) {
        document.body.style.cssText = bodyStyle;
        window.scrollTo(0, scrollY);
      } else if (rootOverflow) {
        document.documentElement.style.overflow = rootOverflow;
      } else {
        document.documentElement.style.removeProperty("overflow");
      }
    }
  }, []);

  const mediator = useMemo(
    () =>
      createModalStackMediator((effect: ModalStackEffect): void => {
        if (effect.type === "scroll-lock") syncBodyScrollLock(effect.locked);
        else effect.close();
      }),
    [syncBodyScrollLock],
  );
  useHierarchyLifetime(mediator);
  const state = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) => {
    if (!isModalStackEvent(event)) return "unhandled";
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const actions = useMemo(
    () => ({
      registerModal(
        close: () => void,
        { lockBodyScroll = true }: ModalRegistrationOptions = {},
      ): ModalRegistration {
        const current = mediator.getSnapshot();
        const id = current.nextId;
        const depth = (current.entries.at(-1)?.depth ?? -1) + 1;
        const event: ModalStackEvent = {
          type: "modal-stack.register",
          entry: { id, depth, close, lockScroll: lockBodyScroll },
        };
        node.dispatch(event);
        return { id, depth };
      },
      unregisterModal(id: number): void {
        const event: ModalStackEvent = { type: "modal-stack.unregister", id };
        node.dispatch(event);
      },
      closeAllModals(): void {
        node.dispatch({ type: "modal-stack.close-all" });
      },
    }),
    [mediator, node],
  );
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      const request: ModalStackEvent = {
        type: "modal-stack.escape",
        isComposing: event.isComposing,
      };
      if (node.dispatch(request).status === "handled") event.preventDefault();
    };
    window.addEventListener("keydown", handleKeyDown);
    return (): void => {
      window.removeEventListener("keydown", handleKeyDown);
      mediator.dispatch({ type: "modal-stack.reset" });
    };
  }, [node, mediator]);
  const modalDepth = state.entries.length;
  const value = useMemo<ModalContextValue>(
    () => ({ ...actions, modalDepth, isModalOpen: modalDepth > 0 }),
    [actions, modalDepth],
  );
  return { node, value };
}
